import {
  Box,
  Center,
  Flex,
  IconButton,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import Button from 'components/atoms/Button/Button';
import Icon from 'components/atoms/Icon/Icon';
import Toast from 'components/atoms/Toast/Toast';
import AuthContext from 'context/AuthProvider';
import WishlistContext from 'context/WishlistProvider';
import { setStorage } from 'helpers/general';
import { get } from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import * as styles from './WishlistButton.module.css';

const WishlistButton = ({ productId, variantId }) => {
  const {
    getAccountWishlists,
    addItemToWishlist,
    checkNameDuplicate,
    saveWishlist,
    removeFromAccountWishlist
  } = useContext(WishlistContext);

  const [showToast, setShowToast] = useState(false);

  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: {
      wishlist_name: '',
      message: ''
    },
    validate: {
      wishlist_name: value => {
        if (!value) {
          return 'Wishlist name is required';
        }
        if (!checkNameDuplicate(value)) {
          return 'Wishlist already exists';
        }
        return null;
      }
    }
  });

  const auth = useContext(AuthContext);
  const isLoggedIn = get(auth, 'state.isLoggedIn', false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: modalIsOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

  const { data: wishlists, refetch } = useQuery(
    ['wishlist', productId, variantId],
    async () => await getAccountWishlists(),
    {
      enabled: isLoggedIn
    }
  );

  const addNewWishlist = e => {
    e.preventDefault();
    const validate = form.validate();

    if (validate.hasErrors) {
      return;
    }

    setSaving(true);
    saveWishlist(form.values.wishlist_name)
      .then(response => {
        if (response.data && response.data.id) {
          saveItemToWishlist(response.data.id);
        }
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setSaving(false);
        onModalClose();
      });
  };

  const saveItemToWishlist = wishlistId => {
    setSaving(true);
    addItemToWishlist(wishlistId, productId, variantId)
      .then(({ status }) => {
        refetch();
        form.setValues({ message: 'Saved to wishlist' });
        setShowToast(true);
      })
      .catch(() => {
        console.error('An error occurred while saving item to wishlist');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const removeItemFromWishlist = () => {
    setSaving(true);
    const wishlistItemId = existsInWishlist.items.find(
      item => item.productId === productId && item.variantId === variantId
    ).id;

    removeFromAccountWishlist(existsInWishlist.id, [], wishlistItemId, null, null)
      .then(() => {
        refetch();
        form.setValues({ message: 'Removed from wishlist' });
        setShowToast(true);
      })
      .catch(() => {
        console.error('An error occurred while removing item from wishlist');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const existsInWishlist = useMemo(() => {
    if (!wishlists?.data) {
      return undefined;
    }

    const wishlist = wishlists.data.find(wishlist => {
      return wishlist.items.find(item => {
        return item.productId === productId && item.variantId === variantId;
      });
    });
    return wishlist;
  }, [wishlists, productId, variantId]);

  return (
    <Box pos={'relative'}>
      {showToast && (
        <Toast duration={1000} message={form.values.message} onClose={() => setShowToast(false)} />
      )}

      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          onClick={
            isLoggedIn
              ? existsInWishlist
                ? removeItemFromWishlist
                : onOpen
              : () => {
                  setStorage(
                    '_afterAuth',
                    JSON.stringify({ action: 'saveWishlist', name: productId }),
                    true
                  );
                  if (typeof window !== 'undefined') {
                    window.location = '/login';
                  }
                }
          }
          minW={'auto'}
          as={IconButton}
          aria-label="Options"
          rounded={'full'}
          w={'30px'}
          variant={'ghost'}
          className={`${styles.wishlist} ${!!existsInWishlist ? styles.active : ''}`}
          icon={<Icon symbol={'heart'} />}
        />
        <MenuList textAlign={'center'}>
          {wishlists?.data?.map(wishlist => (
            <MenuItem
              key={wishlist.id}
              justifyContent={'center'}
              onClick={() => saveItemToWishlist(wishlist.id)}
            >
              {wishlist.name}
            </MenuItem>
          ))}

          <MenuItem justifyContent={'center'} color={'#14607A'} onClick={onModalOpen}>
            + Create New Wishlist
          </MenuItem>
        </MenuList>
      </Menu>
      <Modal isOpen={modalIsOpen} isCentered onClose={onModalClose} size={'3xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody py={10}>
            <Stack textAlign={'center'} spacing={4}>
              <Image loading="lazy" my={0} mx={'auto'} src="/wishlist.png" alt="Wishlist" />
              <h3 classN>New Wishlist</h3>
              <Text>
                Give the list the name, for example after your pet, and you are all set. <bodyr />{' '}
                From now on you will receive sales and offers on this item.
              </Text>
            </Stack>

            <Box mt={'54px'} mb={'27px'}>
              <Flex justifyContent={'space-between'}>
                <Text>Wishlist name</Text>
                <Text color={'#7A8590'} fontSize={12}>
                  REQUIRED
                </Text>
              </Flex>
              <Input rounded={'none'} h={'60px'} {...form.getInputProps('wishlist_name')} />
              <Text mt={2} color={'var(--standard-red)'} fontSize={12}>
                {form.errors.wishlist_name}
              </Text>
            </Box>
            <Center textAlign={'center'}>
              <Button
                disabled={saving}
                loading={saving}
                onClick={addNewWishlist}
                level={'tertiary'}
                type={'button'}
                className={process.env.GATSY_STORE === 'MPS' ? styles.mps_button : ''}
              >
                CREATE WISHLIST
              </Button>
            </Center>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default WishlistButton;
